import {
  type CookieValueTypes,
  type OptionsType,
  getCookie,
  setCookie,
} from 'cookies-next/client'
import { usePostHog } from 'posthog-js/react'
import { useEffect, useMemo, useState } from 'react'
import { v4 } from 'uuid'
import useExpiringLocalStorage from '~/src/hooks/useExpiringLocalStorage'
import { MAX_AGE_OF_COOKIE, TEMP_USER_ID_KEY } from '~/src/lib/constants'

const useGetTempUserId = () => {
  const posthog = usePostHog()

  const existingTempUserIdCookie = getCookie(TEMP_USER_ID_KEY)
  const [tempUserId, setTempUserId] = useState<CookieValueTypes>(
    existingTempUserIdCookie
  )

  // Memoize expiration date to prevent unnecessary re-renders
  const expires = useMemo(
    () => new Date(Date.now() + 1000 * MAX_AGE_OF_COOKIE),
    []
  )

  const [tempUserIdLocalStorage, setTempUserIdLocalStorage] =
    useExpiringLocalStorage<CookieValueTypes>(
      TEMP_USER_ID_KEY,
      existingTempUserIdCookie,
      {
        expires,
        maxAge: MAX_AGE_OF_COOKIE,
      }
    )

  useEffect(() => {
    if (tempUserId) return
    if (!posthog.__loaded) return

    const fallbackId = v4()

    try {
      // const existingTempUserIdValue =
      //   getCookie(TEMP_USER_ID_KEY) || tempUserIdLocalStorage

      const distinctId = posthog.get_distinct_id()
      const uuid = existingTempUserIdCookie || distinctId || fallbackId

      if (!existingTempUserIdCookie) {
        const cookieOptions: OptionsType = {
          expires,
          maxAge: MAX_AGE_OF_COOKIE,
          path: '/',
        }

        if (process.env.NEXT_PUBLIC_VERCEL_ENV === 'production') {
          cookieOptions.domain = '.dupe.com'
        }

        setCookie(TEMP_USER_ID_KEY, uuid, cookieOptions)

        setTempUserIdLocalStorage(uuid)
      } else {
        // alias existing users
        if (
          typeof existingTempUserIdCookie === 'string' &&
          existingTempUserIdCookie !== distinctId
        ) {
          posthog.alias(existingTempUserIdCookie)
        }
      }

      setTempUserId(uuid)
    } catch (error) {
      console.error('Error in useGetTempUserId:', error)
      // Fallback to using uuid if everything else fails
      setTempUserId(fallbackId)
    }
  }, [
    posthog,
    tempUserId,
    tempUserIdLocalStorage,
    expires,
    setTempUserIdLocalStorage,
    existingTempUserIdCookie,
  ])

  const tempUserIdValue = tempUserId?.toString()

  return { tempUserId: tempUserIdValue }
}

export default useGetTempUserId
